<template>
  <div class="landing-page">
    <!-- HERO -->
    <LandingHero @primary="onHeroPrimary" @secondary="onHeroSecondary" />

    <!-- I · THE TRANSFORMATION -->
    <LandingSection :meta="sections.transform" section-id="transform">
      <v-row>
        <v-col cols="12" md="6">
          <h3 class="transform-h transform-h--do">
            <v-icon icon="mdi-check-circle-outline" size="22" /> What you start doing
          </h3>
          <div class="transform-list">
            <div v-for="(item, i) in transformDo" :key="'do' + i" class="transform-item">
              <v-icon :icon="item.icon" size="20" class="transform-icon--do" />
              <span>{{ item.text }}</span>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <h3 class="transform-h transform-h--dont text-error">
            <v-icon icon="mdi-close-circle-outline" size="22" /> What you stop tolerating
          </h3>
          <div class="transform-list">
            <div v-for="(item, i) in transformDont" :key="'dont' + i" class="transform-item">
              <v-icon :icon="item.icon" size="20" class="text-error" />
              <span>{{ item.text }}</span>
            </div>
          </div>
        </v-col>
      </v-row>
    </LandingSection>

    <!-- II · THE CONFIGURATION AGENT -->
    <LandingSection :meta="sections.configure" section-id="configure" variant="band">
      <!-- Static, author-controlled marketing copy (no user input) -->
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-for="(p, i) in configProse" :key="'cp' + i" class="prose text-medium-emphasis" v-html="p"></p>
      <ChatTranscript />
      <div class="feature-trio">
        <div v-for="(f, i) in configFeatures" :key="'cf' + i" class="feature-cell">
          <h4>{{ f.headline }}</h4>
          <p class="text-medium-emphasis">{{ f.text }}</p>
        </div>
      </div>
    </LandingSection>

    <!-- III · THE TOOL CHEST -->
    <LandingSection :meta="sections.tools" section-id="tools">
      <ConnectorCarousel />
    </LandingSection>

    <!-- IV · THE PROVIDERS -->
    <LandingSection :meta="sections.providers" section-id="providers" variant="band">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-for="(p, i) in providersProse" :key="'pp' + i" class="prose text-medium-emphasis" v-html="p"></p>
      <ProviderGrid />
    </LandingSection>

    <!-- V · TWO PATTERNS -->
    <LandingSection :meta="sections.patterns" section-id="patterns">
      <PatternDiagrams />
    </LandingSection>

    <!-- VI · SPECIMEN #1 -->
    <LandingSection :meta="sections.specimen" section-id="specimen">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p v-for="(p, i) in specimenProse" :key="'sp' + i" class="prose text-medium-emphasis" v-html="p"></p>
      <TracePlate />
    </LandingSection>

    <!-- VII · WHAT WE OWE YOU -->
    <LandingSection :meta="sections.promise" section-id="promise">
      <div class="promise-frame bg-surface">
        <div class="promise-eyebrow">
          <v-icon icon="mdi-shield-crown-outline" size="16" /> The Mission Squad promise
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="promise-statement" v-html="promise.statementHtml"></p>
        <v-row class="promise-meta">
          <v-col v-for="(m, i) in promise.meta" :key="'pm' + i" cols="12" md="4">
            <div class="promise-meta-label text-disabled">{{ m.label }}</div>
            <div class="promise-meta-value">{{ m.value }}</div>
          </v-col>
        </v-row>
      </div>
    </LandingSection>

    <!-- VIII · TWO DOORS -->
    <LandingSection :meta="sections.doors" section-id="doors" variant="band-top">
      <v-row class="mt-4">
        <v-col v-for="door in doors" :key="door.id" cols="12" md="6">
          <DoorCard :door="door" @activate="onDoor" />
        </v-col>
      </v-row>
    </LandingSection>
  </div>
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component'
import AnalyticsMixin from '@/mixins/AnalyticsMixin'
import LandingHero from '@/components/landing/LandingHero.vue'
import LandingSection from '@/components/landing/LandingSection.vue'
import ChatTranscript from '@/components/landing/ChatTranscript.vue'
import ConnectorCarousel from '@/components/landing/ConnectorCarousel.vue'
import ProviderGrid from '@/components/landing/ProviderGrid.vue'
import PatternDiagrams from '@/components/landing/PatternDiagrams.vue'
import TracePlate from '@/components/landing/TracePlate.vue'
import DoorCard from '@/components/landing/DoorCard.vue'
import {
  sections,
  transformDo,
  transformDont,
  configProse,
  configFeatures,
  providersProse,
  specimenProse,
  promise,
  doors,
  SectionMeta,
  TransformItem,
  FeatureCell,
  Door
} from '@/components/landing/landingContent'

@Options({
  components: {
    LandingHero,
    LandingSection,
    ChatTranscript,
    ConnectorCarousel,
    ProviderGrid,
    PatternDiagrams,
    TracePlate,
    DoorCard
  }
})
export default class LandingView extends mixins(AnalyticsMixin) {
  sections: Record<string, SectionMeta> = sections
  transformDo: TransformItem[] = transformDo
  transformDont: TransformItem[] = transformDont
  configProse: string[] = configProse
  configFeatures: FeatureCell[] = configFeatures
  providersProse: string[] = providersProse
  specimenProse: string[] = specimenProse
  promise = promise
  doors: Door[] = doors

  mounted() {
    this.trackComponentView('landing_page', { section_count: 8 })
  }

  getStarted(source: string) {
    this.trackButtonClick(source, 'navigate', '/register')
    this.trackConversion('signup_intent', 1, { source })
    this.$router.push('/register')
  }

  onHeroPrimary() {
    this.getStarted('hero_cta')
  }

  onHeroSecondary() {
    this.trackButtonClick('hero_watch_build', 'scroll', '#configure')
    const el = document.getElementById('configure')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  onDoor(id: string) {
    if (id === 'build') {
      this.getStarted('door_build')
      return
    }
    // The briefing — route to contact (closest existing surface).
    this.trackButtonClick('door_briefing', 'navigate', '/contact')
    this.trackEvent('briefing_interest', {
      event_category: 'conversion',
      event_label: 'briefing_cta',
      source: 'landing_page'
    })
    this.$router.push('/contact')
  }
}
</script>

<!-- Landing-wide, theme-aware primitives shared by every landing component
     (unscoped on purpose; everything is namespaced under .landing-page). -->
<style lang="scss">
.landing-page {
  /* mono stack for the design's small-caps/meta text (no new web fonts) */
  --l-mono: 'Roboto Mono', ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  /* visible hairline in both themes — the light theme's border-color token
     matches its background, so borders would otherwise disappear */
  --l-border: rgb(var(--v-theme-border-color));
}
.v-theme--light .landing-page {
  --l-border: rgba(var(--v-theme-on-background), 0.12);
}

/* Accent treatment (design): yellow text on dark; ink with a thick brand-yellow
   underline on light. Applies to static copy injected via v-html too. */
.landing-page .accent {
  color: rgb(var(--v-theme-primary));
}
.v-theme--light .landing-page .accent {
  color: rgb(var(--v-theme-on-background));
  text-decoration: underline;
  text-decoration-color: rgb(var(--v-theme-msq-yellow));
  text-decoration-thickness: 0.16em;
  text-underline-offset: 0.02em;
  text-decoration-skip-ink: none;
}
.v-theme--light .landing-page .prose .accent,
.v-theme--light .landing-page .section-lede .accent {
  text-decoration-thickness: 0.12em;
  text-underline-offset: 0.16em;
}

/* Inline documentation links woven into static ledes/prose (via v-html).
   Dark: brand-yellow with a subtle persistent underline. Light: plain ink text
   with no underline until hover, when a black underline fades in. */
.landing-page .doc-link {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.45);
  transition: border-color 0.2s;
}
.landing-page .doc-link:hover {
  border-bottom-color: rgb(var(--v-theme-primary));
}
.v-theme--light .landing-page .doc-link {
  color: rgb(var(--v-theme-on-background));
  border-bottom: none;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 0.15em;
  transition: text-decoration-color 0.2s;
}
.v-theme--light .landing-page .doc-link:hover {
  text-decoration-color: rgb(var(--v-theme-on-background));
}
</style>

<style scoped lang="scss">
.prose {
  font-size: 17px;
  line-height: 1.6;
  max-width: 70ch;
  margin: 0 0 22px;
  text-wrap: pretty;
}
.prose :deep(em) {
  font-style: italic;
  color: rgb(var(--v-theme-on-surface));
}
.prose :deep(code) {
  font-family: var(--l-mono);
  font-size: 0.84em;
  background: rgb(var(--v-theme-theme-code));
  color: rgb(var(--v-theme-theme-on-code));
  padding: 2px 6px;
  border-radius: 4px;
}

/* section I — transform lists */
.transform-h {
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.02em;
  margin: 0 0 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--l-border);
  display: flex;
  align-items: center;
  gap: 10px;
}
.transform-h--do,
.transform-icon--do {
  color: rgb(var(--v-theme-accent-ink));
}
.transform-item {
  display: flex;
  gap: 14px;
  padding: 16px 0;
  border-bottom: 1px solid var(--l-border);
  font-size: 15px;
  line-height: 1.5;
}
.transform-item:last-child {
  border-bottom: none;
}
.transform-item .v-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

/* section II — feature trio */
.feature-trio {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-top: 52px;
}
.feature-cell h4 {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.005em;
  margin: 0 0 10px;
  padding-top: 16px;
  border-top: 1px solid var(--l-border);
}
.feature-cell p {
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* section VII — promise frame */
.promise-frame {
  border: 1px solid rgb(var(--v-theme-primary));
  padding: 56px;
  border-radius: 12px;
  box-shadow: 0 0 0 1px rgba(var(--v-theme-msq-yellow), 0.4), 0 0 24px rgba(var(--v-theme-msq-yellow), 0.18);
}
.v-theme--light .promise-frame {
  border-color: rgb(var(--v-theme-on-background));
  box-shadow: 0 4px 0 rgb(var(--v-theme-on-background));
}
.promise-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: var(--l-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 24px;
}
.v-theme--light .promise-eyebrow {
  background: rgb(var(--v-theme-msq-yellow));
  color: rgb(var(--v-theme-on-msq-yellow));
  padding: 6px 12px;
  border-radius: 4px;
}
.promise-statement {
  font-size: clamp(28px, 3.6vw, 44px);
  line-height: 1.15;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 32px;
  max-width: 28ch;
  text-wrap: balance;
}
.promise-meta {
  padding-top: 28px;
  border-top: 1px solid var(--l-border);
}
.promise-meta-label {
  font-family: var(--l-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.promise-meta-value {
  font-size: 14px;
  line-height: 1.45;
  font-weight: 500;
}
@media (max-width: 900px) {
  .promise-frame {
    padding: 36px 28px;
  }
  .feature-trio {
    grid-template-columns: 1fr;
    gap: 28px;
  }
}
</style>
